"use client";

import React, {useMemo} from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormGroup, FormHelperText, InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography, useMediaQuery
} from "@mui/material";

import {useLocale, useTranslations} from "next-intl";
import {Control, Controller} from "react-hook-form";
// eslint-disable-next-line import/no-named-as-default
import isEmail, { validateConfirmEmail } from "@/utils/validation/is-email-util";
// eslint-disable-next-line import/no-cycle
import { validatePhoneByCountryCode } from "@/utils/validation/verify-phone-by-locale";
import {Locale} from "@/i18n/routing";
import {ColletteButton, ColletteCheckbox} from "@collette-travel/collette-common-controls";
import {BrochureDisplay} from "@/types/dto/common/brochure";
import {InfoOutlined} from "@mui/icons-material";
import {isCheckedByCode, toggleValue} from "@/utils/request-a-brochure-utils";
import ManagedReCAPTCHA from "@/components/managed-recaptcha/managed-recaptcha";
import {getSortedCountryList} from "@/config/country-codes";
import getStatesByLocale from "@/config/states-config";
import AutoCompleteAddressField from "@/components/forms/auto-complete-address-field/auto-complete-address-field";
import useAutocompleteAddress from "@/hooks/forms/use-autocomplete-address";
import {RABFormData} from "@/types/leads/lead-forms";

interface RABFormProps {
  control: Control<RABFormData, any>;
  handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined;
  brochureDisplay: BrochureDisplay;
  isInvalid: boolean;
  isSubmitting: boolean;
}

const RequestABrochureForm = ({
  control,
  handleSubmit,
  brochureDisplay,
  isInvalid,
  isSubmitting
}: RABFormProps) => {
  const t = useTranslations("FORMS");
  const locale = useLocale() as Locale;
  const shrinkReCaptcha = useMediaQuery("(max-width:425px)");

  const countryCodes = getSortedCountryList(locale);

  const {
    minimumOrder,
    maximumOrder,
    brochureOrder
  } = brochureDisplay;
  
  const stateOptions = useMemo(() => getStatesByLocale(locale).map((state) => state.name), [locale]);

  const {
    handleChange: handleAddressChange,
    addressOptions
  } = useAutocompleteAddress();

  const filteredOrder = brochureOrder.filter((brochure) => (brochure.brochureIsOrderable));

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="rab-form"
      data-form-name="Request a Brochure"
      data-form-id="request-a-brochure-form"
      data-track="visibility"
    >
      <Stack
        gap="2.4rem"
        paddingY="2rem"
      >
        <Stack
          sx={{
            flexDirection: { xs: "column-reverse", md: "row" },
            alignItems: "flex-start",
            gap: "2.4rem"
          }}
        >
          <Stack
            sx={{
              gap: "4rem",
              width: {xs: "100%", md: "50%"}
            }}
          >
            {/* POSTAL INFO */}
            <Stack
              sx={{
                gap: "1.6rem"
              }}
            >
              <Typography
                variant="h6"
                data-testid="rab-postal-info-header"
              >
                {t("Postal Information")}
              </Typography>
              {/* Title */}
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <InputLabel id="rab-title-field-label">{t("Title")}</InputLabel>
                    <Select
                      {...field}
                      data-testid="rab-title-field"
                      aria-label={t("Title")}
                      label={t("Title")}
                      labelId="rab-title-field-label"
                      sx={{
                        width: "100%"
                      }}
                      value={field.value}
                    >
                      {["Dr.", "Mrs.", "Mr.", "Ms."].map((title) => (
                        <MenuItem
                          data-testid="rab-title-field-option"
                          key={title}
                          value={title}
                        >
                          {title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {/* First Name */}
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: t("Please complete this required field")
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    data-testid="rab-first-name-field"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    aria-label={t("First Name")}
                    placeholder={t("First Name")}
                    label={t("First Name")}
                    sx={{
                      width: "100%"
                    }}
                  />
                )}
              />
              {/* Last Name */}
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: t("Please complete this required field")
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    data-testid="rab-last-name-field"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    aria-label={t("Last Name")}
                    placeholder={t("Last Name")}
                    label={t("Last Name")}
                    sx={{
                      width: "100%"
                    }}
                  />
                )}
              />
              {/* Email */}
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t("Please complete this required field"),
                  validate: (value) => isEmail(value) || t("Email must be formatted correctly")
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    data-testid="rab-email-field"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    aria-label={t("Email")}
                    placeholder={t("Email")}
                    label={t("Email")}
                    sx={{
                      width: "100%"
                    }}
                  />
                )}
              />
              {/* Confirm Email */}
              <Controller
                name="confirmEmail"
                control={control}
                rules={{
                  required: t("Please complete this required field"),
                  validate: (value, formValues) => validateConfirmEmail(value, formValues.email, t)
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    data-testid="rab-confirm-email-field"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    aria-label={t("Confirm Email")}
                    placeholder={t("Confirm Email")}
                    label={t("Confirm Email")}
                    sx={{
                      width: "100%"
                    }}
                  />
                )}
              />
              {/* Country Code */}
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <InputLabel id="rab-country-code-field-label">{t("Country Code")}</InputLabel>
                    <Select
                      {...field}
                      data-testid="rab-country-code-field"
                      aria-label={t("Country Code")}
                      label={t("Country Code")}
                      labelId="rab-country-code-field-label"
                      sx={{
                        width: "100%"
                      }}
                      value={field.value.ISO2}
                      onChange={(event) => {
                        const selected = countryCodes.find(
                          (c) => c.ISO2 === event.target.value
                        );
                        field.onChange(selected);
                      }}
                      renderValue={(val) => {
                        const country = countryCodes.find((c) => c.ISO2 === val);
                        return country ? `+${country.callingCode} ${country.name}` : "";
                      }}
                      defaultValue={field.value.ISO2 ?? "US"}
                    >
                      {countryCodes.map((country) => (
                        <MenuItem
                          key={country.ISO2}
                          value={country.ISO2}
                        >
                          {`+${country.callingCode} ${country.name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {/* Phone */}
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: t("Please complete this required field"),
                  validate: (value, formValues) => validatePhoneByCountryCode(value, formValues.countryCode.ISO2, t, false)
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    data-testid="rab-phone-field"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    aria-label={t("Phone")}
                    placeholder={t("Phone")}
                    label={t("Phone")}
                    sx={{
                      width: "100%"
                    }}
                  />
                )}
              />
            </Stack>

            {/* MAILING INFO */}

            <Stack
              sx={{
                gap: "1.6rem"
              }}
            >
              <Typography
                variant="h6"
                data-testid="rab-mailing-info-header"
              >
                {t("Mailing Information")}
              </Typography>
              {/* AUTOCOMPLETE ADDRESS FIELD */}
              {locale === "en-au" && (
                <Controller
                  name="experianAddress"
                  control={control}
                  rules={{
                    required: t("Please complete this required field"),
                    validate: (value) =>
                      addressOptions.map((o) => o.suggestion).includes(value?.suggestion ?? "") || t("Please select a valid address from the list")
                  }}
                  render={({ field, fieldState }) => (
                    <AutoCompleteAddressField
                      testId="rab-autocomplete-address"
                      handleChange={handleAddressChange}
                      addressOptions={addressOptions}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                />
              )}
              {locale !== "en-au" && (
                <>
                  {/* Street Address */}
                  <Controller
                    name="address"
                    control={control}
                    rules={{
                      required: t("Please complete this required field")
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        required
                        autoComplete="address-line1"
                        data-testid="rab-address-field"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        aria-label={t("Street Address")}
                        placeholder={t("Street Address")}
                        label={t("Street Address")}
                        sx={{
                          width: "100%"
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="address2"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        autoComplete="address-line2"
                        data-testid="rab-address-line-2-field"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        aria-label={t("Street Address Line 2")}
                        placeholder={t("Street Address Line 2")}
                        label={t("Street Address Line 2")}
                        sx={{
                          width: "100%"
                        }}
                      />
                    )}
                  />

                  {/* Country */}
                  <Controller
                    name="country"
                    control={control}
                    rules={{
                      required: t("Please complete this required field")
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        required
                        disabled
                        data-testid="rab-country-field"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        aria-label={t("Country")}
                        placeholder={t("Country")}
                        label={t("Country")}
                        sx={{
                          width: "100%"
                        }}
                      />
                    )}
                  />
                  {/* City */}
                  <Controller
                    name="city"
                    control={control}
                    rules={{
                      required: t("Please complete this required field")
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        required
                        data-testid="rab-city-field"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        aria-label={t("City")}
                        placeholder={t("City")}
                        label={t("City")}
                        sx={{
                          width: "100%"
                        }}
                      />
                    )}
                  />
                  {/* State */}
                  <Controller
                    name="state"
                    control={control}
                    rules={{
                      required: t("Please complete this required field"),
                      validate: (value) =>
                        stateOptions.includes(value) || t("Please select a valid state from the list")
                    }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        data-testid="rab-state-field_autocomplete"
                        value={field?.value}
                        options={stateOptions}
                        slotProps={{
                          listbox: {
                            className: "rab-state-popup"
                          }
                        }}                        
                        onInputChange={async (_, value) => {
                          if (!value) return;
                          field?.onChange(value);
                        }}
                        onChange={(_, newValue) => {
                          // When user selects an option
                          field?.onChange(newValue);
                        }}
                        noOptionsText={(
                          <Typography color="text.primary">
                            {t("Please enter a valid state")}
                          </Typography>
                        )}
                        renderInput={(params) => (
                          <TextField
                            data-testid="rab-state-field_text-field"
                            {...params}
                            autoComplete="state"
                            required
                            label={t("State")}
                            error={!!fieldState?.error}
                            helperText={fieldState?.error?.message}
                            aria-label={t("State")}
                            placeholder={t("State")}
                          />
                        )}
                      />
                    )}
                  />
                  {/* Zip Code */}
                  <Controller
                    name="zipCode"
                    control={control}
                    rules={{
                      required: t("Please complete this required field")
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        required
                        data-testid="rab-zip-code-field"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        aria-label={t("Zip Code")}
                        placeholder={t("Zip Code")}
                        label={t("Zip Code")}
                        sx={{
                          width: "100%"
                        }}
                      />
                    )}
                  />
                </>
            )}
            </Stack>
          </Stack>

          {/* Checkboxes */}
          <Controller
            name="customerLeadBrochures"
            control={control}
            rules={{
             required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => {
              const { value, onBlur } = field;

              const selectedCount = value.length;

              const hasInteracted = fieldState.isDirty || fieldState.isTouched;

              return (
                <Stack
                  gap="1.2rem"
                  sx={{ width: { xs: "100%", md: "50%" } }}
                >
                  <Typography
                    variant="h6"
                    data-testid="rab-selected-brochures-header"
                  >
                    {t("RAB.Selected Brochures")}
                  </Typography>
                  <Typography
                    data-testid="rab-selected-brochures-copy"
                  >
                    {t("RAB.Select up to", { maximumOrder })}
                  </Typography>

                  {(((selectedCount < minimumOrder) && (hasInteracted || isInvalid)) || selectedCount === maximumOrder) && (
                    <Box
                      data-testid="rab-brochure-select-error"
                      sx={{
                        backgroundColor: "error.100",
                        borderRadius: "8px",
                        color: "error.dark",
                        padding: "12px",
                        display: "flex",
                        gap: "16px"
                      }}
                    >
                      <InfoOutlined sx={{ height: "24px", width: "24px" }} />
                      <Typography
                        sx={{
                          letterSpacing: "0.04rem"
                        }}
                      >
                        {selectedCount === 0
                          ? t("RAB.Please select at least brochure", {
                            minimumOrder
                          })
                          : t("RAB.Max brochures per order", {
                            maximumOrder
                          })}
                      </Typography>
                    </Box>
                  )}

                  <FormControl
                    error={!!fieldState.error}
                    onBlur={onBlur}
                  >
                    <FormGroup>
                      {filteredOrder.map((brochure) => {
                        const code = brochure.brochureCode;
                        const isChecked = isCheckedByCode(value, code);
                        const disabled = !isChecked && selectedCount >= maximumOrder;

                        return (
                          <ColletteCheckbox
                            label={brochure.brochureName}
                            key={`rab-brochure-option_${code}`}
                            name={code}
                            testId={`rab-brochure-option_${code}`}
                            checked={isChecked}
                            onChange={(e) => {
                              const updated = toggleValue(
                                value,
                                brochure,
                                e.target.checked,
                                maximumOrder
                              );
                              field.onChange(updated);
                            }}
                            sx={{
                              marginRight: "0.6rem",
                              alignItems: "flex-start"
                            }}
                            disabled={disabled}
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                </Stack>
              );
            }}
          />
        </Stack>
        {/* OTHER INFO */}

          <Controller
            name="isTravelAgent"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <Stack
                  sx={{
                    gap: "1.2rem",
                    alignItems: {xs: "flex-start", sm: "center"},
                    flexDirection: {xs: "column", sm: "row"}
                  }}
                >
                  <Typography
                    sx={{
                      "&::after": { content: "\"*\"", ariaHidden: "true" }
                    }}
                  >
                    {t("Are you a travel advisor?")}
                  </Typography>
                <RadioGroup
                  {...field}
                  value={field.value ?? ""}
                  onChange={(_, value) => field.onChange(value)}
                  row
                  aria-label={t("Are you a travel advisor?")}
                  data-testid="rab-travel-advisor-radio-group"
                  sx={{
                    gap: "3.2rem"
                  }}
                >
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("Yes")}
                    value="yes"
                  />
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("No")}
                    value="no"
                  />
                </RadioGroup>
                </Stack>
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />

        <Stack gap={1}>
          <Controller
            name="optInEmail"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="rab-opt-in-email-checkbox-field"
                label={t("Email opt in")}
                onChange={(e) => field.onChange(e.target.checked)}
                sx={{
                  marginRight: "0.6rem",
                  alignItems: "flex-start"
                }}
              />
            )}
          />
          <Controller
            name="withMoreTravelers"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="rab-with-more-travelers-checkbox-field"
                label={t("Traveling with 8 or more people")}
                onChange={(e) => field.onChange(e.target.checked)}
                sx={{
                  marginRight: "0.6rem",
                  alignItems: "flex-start"
                }}
              />
            )}
          />
        </Stack>
        <Controller
          name="recaptchaToken"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Box
                sx={{
                  transformOrigin: "0 0",
                  transform: `scale(${shrinkReCaptcha ? "0.7" : "1" })`
                }}
              >
                <ManagedReCAPTCHA
                  size="normal"
                  sitekey="6LfV8zIpAAAAALFDQIb2-f4xUO3PrI8vVhrgwA9x"
                  onChange={(token) => field.onChange(token)}
                />
              </Box>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* VALIDATION MESSAGE */}
        {isInvalid && (
          <Box
            sx={{
              padding: "0.4rem 1rem",
              backgroundColor: "error.100",
              borderRadius: "1rem",
              color: "error.dark"
            }}
          >
            <Typography>
              {t("Please complete all required fields")}
            </Typography>
          </Box>
        )}

        {/* SUBMIT BUTTON */}
        <Box width="100%" display="flex" justifyContent="end">
          <ColletteButton
            label={t("Submit")}
            disabled={isSubmitting}
            aria-label={t("Submit")}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            testId="rab-submit-button"
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default RequestABrochureForm;